import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { StatusBadge } from "../components/Semaforo";
import { formatCLP } from "../lib/format";
import { Plus, MapPin, Trash, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { TIPO_VINCULO_COM } from "../lib/constants";
import LocationSelector, { LocationFilter } from "../components/LocationSelector";
import MapPicker from "../components/MapPicker";
import { useConfirm } from "../components/ConfirmDialog";

export default function Communities() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({ q: "", region: "", provincia: "", comuna: "", tipo_vinculo: "" });
  const [form, setForm] = useState(emptyForm());

  function emptyForm() {
    return {
      name: "", tipo_vinculo: "Operacional", description: "",
      habitantes: 0, hombres: 0, mujeres: 0,
      location: { pais: "Chile", region: "", provincia: "", comuna: "", localizacion: "", latitude: "", longitude: "" },
    };
  }

  const load = async () => {
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.region) params.region = filters.region;
    if (filters.comuna) params.comuna = filters.comuna;
    if (filters.tipo_vinculo) params.tipo_vinculo = filters.tipo_vinculo;
    const { data } = await api.get("/communities", { params });
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    try {
      const payload = { ...form };
      payload.habitantes = +payload.habitantes || 0;
      payload.hombres = +payload.hombres || 0;
      payload.mujeres = +payload.mujeres || 0;
      payload.location.latitude = payload.location.latitude ? +payload.location.latitude : null;
      payload.location.longitude = payload.location.longitude ? +payload.location.longitude : null;
      await api.post("/communities", payload);
      toast.success("Comunidad creada");
      setOpen(false);
      setForm(emptyForm());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Eliminar comunidad", description: "¿Deseas eliminar esta comunidad? Esta acción no se puede deshacer.", danger: true }))) return;
    await api.delete(`/communities/${id}`);
    load();
  };

  return (
    <div data-testid="communities-page">
      <PageHeader
        title="Comunidades"
        description="Repositorio de comunidades con datos censales, integrantes, líderes, proyectos y compromisos asociados."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-community-btn" className="bg-slate-900 hover:bg-slate-800 text-white">
                <Plus weight="bold" /> Nueva comunidad
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Nueva comunidad</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nombre</Label>
                  <Input data-testid="community-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <Label>Tipo de vínculo</Label>
                  <Select value={form.tipo_vinculo} onValueChange={(v) => setForm({ ...form, tipo_vinculo: v })}>
                    <SelectTrigger data-testid="community-vinculo-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPO_VINCULO_COM.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Habitantes</Label>
                  <Input type="number" value={form.habitantes} onChange={(e) => setForm({ ...form, habitantes: e.target.value })} />
                </div>
                <div>
                  <Label>Hombres</Label>
                  <Input type="number" value={form.hombres} onChange={(e) => setForm({ ...form, hombres: e.target.value })} />
                </div>
                <div>
                  <Label>Mujeres</Label>
                  <Input type="number" value={form.mujeres} onChange={(e) => setForm({ ...form, mujeres: e.target.value })} />
                </div>
                <LocationSelector
                  value={{ region: form.location.region, provincia: form.location.provincia, comuna: form.location.comuna }}
                  onChange={(next) => setForm({ ...form, location: { ...form.location, ...next } })}
                  testidPrefix="community-loc"
                />
                <div>
                  <Label>Localización</Label>
                  <Input value={form.location.localizacion} onChange={(e) => setForm({ ...form, location: { ...form.location, localizacion: e.target.value } })} placeholder="Sector, sub-localidad…" />
                </div>
                <div className="col-span-2">
                  <Label className="mb-1.5 block">Ubicación geográfica</Label>
                  <MapPicker
                    value={{ latitude: form.location.latitude, longitude: form.location.longitude }}
                    onChange={(next) => setForm({ ...form, location: { ...form.location, latitude: next.latitude, longitude: next.longitude } })}
                    autoQuery={[form.location.comuna, form.location.region, "Chile"].filter(Boolean).join(", ")}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Descripción</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={submit} data-testid="save-community-btn" className="bg-slate-900 text-white">Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="px-6 py-6 space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 rounded-md border border-slate-200 bg-white p-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Buscar</Label>
            <Input data-testid="filter-q" placeholder="Nombre…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          </div>
          <LocationFilter
            region={filters.region}
            provincia={filters.provincia}
            comuna={filters.comuna}
            includeProvincia
            onChange={(next) => setFilters({ ...filters, ...next })}
          />
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Vínculo</Label>
            <Select value={filters.tipo_vinculo || "__all__"} onValueChange={(v) => setFilters({ ...filters, tipo_vinculo: v === "__all__" ? "" : v })}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                {TIPO_VINCULO_COM.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="secondary" onClick={load} data-testid="apply-filters-btn">Aplicar</Button>
        </div>

        {/* Table */}
        <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Vínculo</TableHead>
                <TableHead>Región / Comuna</TableHead>
                <TableHead className="text-right">Habitantes</TableHead>
                <TableHead className="text-right">Inversión total</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">Sin comunidades registradas.</TableCell></TableRow>
              )}
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`community-row-${c.id}`}>
                  <TableCell className="font-medium">
                    <Link to={`/comunidades/${c.id}`} className="hover:underline flex items-center gap-1">
                      {c.name}
                    </Link>
                    {c.localizacion && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={12} /> {c.localizacion}</div>}
                  </TableCell>
                  <TableCell>
                    <StatusBadge tone={c.tipo_vinculo === "Operacional" ? "info" : "neutral"}>{c.tipo_vinculo}</StatusBadge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    <div>{c.region || "—"}</div>
                    <div className="text-xs text-slate-500">{c.comuna || ""}</div>
                  </TableCell>
                  <TableCell className="text-right font-mono">{c.habitantes || 0}</TableCell>
                  <TableCell className="text-right font-mono text-slate-700">{formatCLP(c.inversion_total)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Link to={`/comunidades/${c.id}`}>
                        <Button size="sm" variant="ghost" data-testid={`view-community-${c.id}`}><ArrowSquareOut size={16} /></Button>
                      </Link>
                      <Button size="sm" variant="ghost" onClick={() => remove(c.id)} data-testid={`del-community-${c.id}`}>
                        <Trash size={16} className="text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
